import React, { useState, useMemo, useEffect, useCallback } from "react";
import { MessageSquare, Trash2 } from "lucide-react";
import { PageHeader, SearchInput, ConfirmModal } from "../../ui";
import { FilterBar, FilterSelect } from "../../ui/FilterBar";
import apiClient from "../../../utils/apiClient";
import "./sales-leads.scss";

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "converted", label: "Converted" },
  { value: "closed", label: "Closed" },
];

const STATUS_BADGE = {
  new: "sa-badge-info",
  contacted: "sa-badge-warning",
  converted: "sa-badge-success",
  closed: "sa-badge-muted",
};

const SalesLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [noteModal, setNoteModal] = useState(null); // lead id
  const [noteText, setNoteText] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await apiClient.get("/sa/sales-leads");
      setLeads(res.data.leads || []);
    } catch {
      // keep empty
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = useMemo(() => {
    let list = [...leads];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (l) =>
          l.name?.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q) ||
          l.instituteName?.toLowerCase().includes(q) ||
          l.phone?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") list = list.filter((l) => l.status === statusFilter);
    return list;
  }, [leads, search, statusFilter]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiClient.put(`/sa/sales-leads/${id}`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l._id === id ? { ...l, status: newStatus } : l)));
    } catch {
      // handle error
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleteLoading(true);
      await apiClient.delete(`/sa/sales-leads/${deleteId}`);
      setLeads((prev) => prev.filter((l) => l._id !== deleteId));
      setDeleteId(null);
    } catch {
      // handle error
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteModal || !noteText.trim()) return;
    try {
      setNoteLoading(true);
      const res = await apiClient.put(`/sa/sales-leads/${noteModal}`, { note: noteText.trim() });
      setLeads((prev) => prev.map((l) => (l._id === noteModal ? res.data.lead : l)));
      setNoteModal(null);
      setNoteText("");
    } catch {
      // handle error
    } finally {
      setNoteLoading(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="sa-sales-leads">
      <PageHeader
        title="Sales Leads"
        description={`${leads.length} leads from the sales website`}
        icon={MessageSquare}
      />

      <FilterBar
        actions={<SearchInput value={search} onChange={setSearch} placeholder="Search leads…" />}
      >
        <FilterSelect label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
      </FilterBar>

      <div className="sa-table-card">
        <div className="sa-table-scroll">
          <table className="sa-mini-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Institute</th>
                <th>Message</th>
                <th>Status</th>
                <th>Date</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-5 text-muted" style={{ fontSize: "0.8125rem" }}>
                    No leads match your filters
                  </td>
                </tr>
              ) : (
                filtered.map((lead) => (
                  <tr key={lead._id}>
                    <td className="fw-medium">{lead.name}</td>
                    <td>{lead.email}</td>
                    <td>{lead.phone || "—"}</td>
                    <td>{lead.instituteName || "—"}</td>
                    <td className="sl-msg-cell" title={lead.message}>{lead.message || "—"}</td>
                    <td>
                      <select
                        className={`sl-status-select ${STATUS_BADGE[lead.status] || ""}`}
                        value={lead.status}
                        onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="text-muted">{fmtDate(lead.createdAt)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          title="Add note"
                          onClick={() => { setNoteModal(lead._id); setNoteText(""); }}
                        >
                          <MessageSquare size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => setDeleteId(lead._id)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes panel (shown below table when a lead has notes) */}
      {noteModal && (
        <div className="sl-note-modal-backdrop" onClick={() => setNoteModal(null)}>
          <div className="sl-note-modal" onClick={(e) => e.stopPropagation()}>
            <h6 className="mb-3">Add Note</h6>
            <textarea
              className="form-control mb-2"
              rows={3}
              placeholder="Type a note…"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setNoteModal(null)}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={handleAddNote} disabled={noteLoading || !noteText.trim()}>
                {noteLoading ? "Saving…" : "Save Note"}
              </button>
            </div>
            {/* Existing notes */}
            {(() => {
              const lead = leads.find((l) => l._id === noteModal);
              if (!lead?.notes?.length) return null;
              return (
                <div className="sl-existing-notes mt-3">
                  <small className="text-muted fw-medium">Previous Notes</small>
                  {lead.notes.map((n, i) => (
                    <div key={i} className="sl-note-item">
                      <span>{n.note}</span>
                      <small className="text-muted">{fmtDate(n.createdAt)}</small>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteId}
        onHide={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Lead"
        message="This will permanently delete this sales lead. This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default SalesLeadsPage;
